<template>
    <!-- Sidebar- Desktop: sticky / Mobile: relative -->
    <aside class="bg-white rounded-xl p-6 shadow-md lg:sticky lg:top-[140px] lg:h-fit w-full lg:w-64 xl:w-80 mb-6 lg:mb-0">
        <!-- Titre de section -->
        <h3 class="text-base font-bold text-[#2c3e50] mb-4 pb-2 border-b-2 border-gray-100">
            <span v-if="!loading">Filtres de recherche</span>
            <span v-else class="shimmer shimmer-title"></span>
        </h3>

        <!-- Filtres -->
        <div class="space-y-4">
            <!-- Recherche -->
            <div class="relative">
                <input
                    v-if="!loading"
                    type="text"
                    placeholder="Rechercher un événement..."
                    class="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    v-model="searchQuery"
                >
                <div v-else class="shimmer shimmer-input"></div>

                <i v-if="!loading" class="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"></i>
                <div v-else class="shimmer shimmer-icon-small absolute left-3 top-1/2 transform -translate-y-1/2"></div>
            </div>

            <!-- Catégorie -->
            <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                    <i v-if="!loading" class="fas fa-tags mr-2 text-[#2c3e50]"></i>
                    <span v-if="!loading">Catégorie</span>
                    <div v-else class="flex items-center">
                        <div class="shimmer shimmer-icon-small mr-2"></div>
                        <span class="shimmer shimmer-label"></span>
                    </div>
                </label>
                <XSelect
                    v-if="!loading"
                    placeholder="Catégorie"
                    name="category"
                    :options="categories"
                    v-model="selectedCategory"
                />
                <div v-else class="shimmer shimmer-select"></div>
            </div>
            <!-- Lieu -->
            <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                    <i v-if="!loading" class="fas fa-map-marker-alt mr-2 text-[#2c3e50]"></i>
                    <span v-if="!loading">Lieu</span>
                    <div v-else class="flex items-center">
                        <div class="shimmer shimmer-icon-small mr-2"></div>
                        <span class="shimmer shimmer-label"></span>
                    </div>
                </label>
                <XSelect
                    v-if="!loading"
                    placeholder="Lieu"
                    name="lieu"
                    :options="computedLocation"
                    v-model="selectedLocation"
                />
                <div v-else class="shimmer shimmer-select"></div>
            </div>

            <!-- Statut - CODE INCHANGÉ -->
            <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                    <i v-if="!loading" class="fas fa-info-circle mr-2 text-[#2c3e50]"></i>
                    <span v-if="!loading">Statut</span>
                    <div v-else class="flex items-center">
                        <div class="shimmer shimmer-icon-small mr-2"></div>
                        <span class="shimmer shimmer-label"></span>
                    </div>
                </label>
                <div v-if="!loading" class="space-y-2">
                    <XInputCheckBox
                        id="enrolled"
                        title="Inscription obligatoire"
                        subtitle=""
                        v-model="statusFilters.enrolled"
                    />
                    <div class="flex flex-row gap-2">
                        <XInputCheckBox
                            style="width: 43% !important;"
                            id="full"
                            title="Récurrent"
                            subtitle=""
                            v-model="statusFilters.is_recurrent"
                        />
                        <XInputCheckBox
                            style="width: 58% !important;"
                            id="canceled"
                            title="Mise à avant"
                            subtitle=""
                            v-model="statusFilters.is_feature"
                        />
                    </div>

                </div>
                <div v-else class="space-y-2">
                    <div v-for="i in 4" :key="i" class="flex items-center gap-2">
                        <div class="shimmer shimmer-checkbox"></div>
                        <div class="shimmer shimmer-circle"></div>
                        <span class="shimmer shimmer-status-text"></span>
                    </div>
                </div>
            </div>

            <!-- Boutons d'action (optionnels) - CODE INCHANGÉ -->
            <div v-if="!loading" class="flex gap-2 pt-4 border-t border-gray-100">
                <button @click="applyFilters" class="flex-1 cursor-pointer bg-[#2A4493] hover:bg-[#2A4493] text-white py-2 px-4 rounded-lg text-sm transition-colors">
                    <i class="fas fa-filter mr-2"></i>Appliquer
                </button>
                <button @click="resetFilters" class="px-4 cursor-pointer py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm transition-colors">
                    <i class="fas fa-times"></i>
                </button>
            </div>
            <div v-else class="pt-4 border-t border-gray-100 space-y-2">
                <div class="shimmer shimmer-button"></div>
                <div class="shimmer shimmer-button"></div>
            </div>
        </div>
    </aside>
</template>

<script setup>
import {ref, computed} from 'vue'
import XInputCheckBox from "@/vue/Form/X-InputCheckBox.vue";
import XSelect from "@/vue/Form/XSelect.vue";

const props = defineProps({
    loading: {
        type: Boolean,
        default: false
    },
    categories: {
        type: [Array, Object, null],
        default: [],
    },
    location: {
        type: [Array, Object, null],
        default: [],
    }
})

const emit = defineEmits(['filtersChanged','filtersInitialize'])
const computedLocation = computed(() => props.location)

// État des filtres
const searchQuery = ref('')
const selectedCategory = ref('')
const selectedPeriod = ref('')
const selectedLocation = ref('')
const statusFilters = ref({
    enrolled: false,
    is_feature: false,
    is_recurrent: false,
})

// Méthodes
const applyFilters = () => {
    const filters = {
        search: searchQuery.value,
        category: selectedCategory.value,
        period: selectedPeriod.value,
        location: selectedLocation.value,
        status: statusFilters.value
    }
    emit('filtersChanged', filters)
}

const resetFilters = () => {
    searchQuery.value = ''
    selectedCategory.value = ''
    selectedPeriod.value = ''
    selectedLocation.value = ''
    statusFilters.value = {
        enrolled: false,
        is_feature: false,
        is_recurrent: false,
    }
    emit('filtersInitialize', {})
}
</script>

<style scoped>
.shimmer {
    display: inline-block;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: shimmerAnim 1.5s infinite;
    border-radius: 4px;
}

.shimmer-title {
    height: 18px;
    width: 150px;
    margin-bottom: 4px;
}

.shimmer-input {
    height: 38px;
    width: 100%;
    border-radius: 8px;
}

.shimmer-select {
    height: 38px;
    width: 100%;
    border-radius: 8px;
}

.shimmer-label {
    height: 14px;
    width: 80px;
}

.shimmer-icon-small {
    height: 14px;
    width: 14px;
    border-radius: 2px;
}

.shimmer-checkbox {
    height: 16px;
    width: 16px;
    border-radius: 2px;
}

.shimmer-circle {
    height: 12px;
    width: 12px;
    border-radius: 50%;
}

.shimmer-status-text {
    height: 14px;
    width: 120px;
}

.shimmer-button {
    height: 38px;
    width: 100%;
    border-radius: 8px;
}

@keyframes shimmerAnim {
    0% {
        background-position: -200% 0;
    }
    100% {
        background-position: 200% 0;
    }
}
</style>
